import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatTabsModule } from '@angular/material/tabs';
import { CommonModule } from '@angular/common';
import { BreadcrumbComponent } from '../breadcrumb/breadcrumb.component';
import { ChannelPricing, Variant, Channel } from '../models/combined-interfaces.model';
import { AdminCommonHttpService } from '../service/admin-common-http.service';
import { MatTooltip } from '@angular/material/tooltip';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatDialog } from '@angular/material/dialog';
import { CreateVariantDialogComponent } from '../variant-detail/create-variant-dialog/create-variant-dialog.component';
import { MatIcon } from '@angular/material/icon';


@Component({
  selector: 'app-product-detail',
  standalone: true,
  imports: [MatTabsModule, CommonModule, BreadcrumbComponent, MatTooltip, MatFormField, MatLabel, MatSelect, MatOption, CreateVariantDialogComponent, MatIcon],
  templateUrl: './product-detail.component.html',
  styleUrls: ['./product-detail.component.scss']
})
export class ProductDetailComponent {
  productId: number | null = null;
  productName: string | null = null;
  product: any = null;
  variants: Variant[] = [];
  selectedTabIndex = 0;
  channels: Channel[] = [];
  selectedChannel: string = '';


  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private commonApiService: AdminCommonHttpService,
    private dialog: MatDialog
  ) { }

  async ngOnInit(): Promise<void> {
    this.productId = Number(this.route.snapshot.paramMap.get('productId'));

    this.route.queryParamMap.subscribe(params => {
      const name = params.get('productName');
      const tab = params.get('tab');

      if (name) {
        this.productName = decodeURIComponent(name);
        console.log('[Breadcrumb] Product name set to:', this.productName);
      } else {
        console.log('FALLBACK');
      }

      if (tab === 'variants') {
        this.selectedTabIndex = 1;
      }
    });

    await this.loadChannels();

    if (this.productId) {
      await this.loadProduct();
      await this.loadVariants();
    }
  }

  async loadChannels(): Promise<void> {
    try {
      const result = await this.commonApiService.getAllChannels();
      this.channels = result.filter(ch => ch.is_active);
      if (this.channels.length > 0) {
        this.selectedChannel = this.channels[0].channel_code;
      }
    } catch (err) {
      console.error('Failed to load channels', err);
    }
  }

  getChannelDisplayName(code: string): string {
    return this.channels.find(c => c.channel_code === code)?.display_name || code;
  }

  async loadProduct(): Promise<void> {
    try {
      this.product = await this.commonApiService.getProductById(this.productId!);
    } catch (error) {
      console.error('Failed to load product metadata:', error);
    }
  }

  async loadVariants(): Promise<void> {
    if (this.productId) {
      try {
        this.variants = await this.commonApiService.getVariantsByProduct(this.productId);
      } catch (error) {
        console.error('Failed to load product variants:', error);
      }
    }
  }

  goToVariantDetail(variant: Variant): void {
    this.router.navigate(
      ['/admin/manage-products', this.productId, 'variant', variant.variant_id],
      {
        queryParams: {
          productName: this.product?.name,
          variantName: variant.variant_key
        }
      }
    );
  }

  getAllAttributeKeys(): string[] {
    const allKeys = new Set<string>();
    this.variants.forEach(variant => {
      if (variant.attributes) {
        Object.keys(variant.attributes).forEach(key => allKeys.add(key));
      }
    });
    return Array.from(allKeys);
  }

  getChannelPrice(variant: Variant, channel: string): string {
    const entry = variant.pricing?.find(p => p.channel_code === channel);
    if (!entry) return '—';
    return entry.is_on_sale && entry.sale_price != null
      ? `$${entry.sale_price} (Sale)`
      : `$${entry.price}`;
  }


  getChannelPricing(variant: Variant, channelCode: string): ChannelPricing | null {
    return variant.pricing?.find(p => p.channel_code === channelCode) || null;
  }


  hasChannelPricing(variant: Variant, channel: string): boolean {
    return !!variant.pricing?.some(p => p.channel_code === channel);
  }

  getTooltipForChannel(priceInfo: ChannelPricing | null, variant: Variant): string {
    if (!priceInfo) return `Base Price: $${variant.price}`;
    if (priceInfo.is_on_sale && priceInfo.sale_price != null) {
      return `Original: $${priceInfo.price}`;
    }
    return `Channel Price: $${priceInfo.price}`;
  }

  getGroupNames(groups?: { id: number; name: string }[] | null): string {
    return Array.isArray(groups) && groups.length ? groups.map(g => g.name).join(', ') : '—';
  }

  getCategoryNames(categories?: { id: number; name: string }[] | null): string {
    return Array.isArray(categories) && categories.length ? categories.map(g => g.name).join(', ') : '—';
  }

  createVariant(): void {
    const dialogRef = this.dialog.open(CreateVariantDialogComponent, {
      width: '600px',
      data: {}
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result && this.productId) {
        try {
          const payload = {
            product_id: this.productId,
            ...result
          };
          await this.commonApiService.createProductVariant(payload);
          await this.loadVariants();
        } catch (err) {
          console.error('Error creating variant:', err);
        }
      }
    });
  }


  async deleteVariant(variant: Variant, event: MouseEvent) {
    event.stopPropagation(); // prevent row click

    const confirmDelete = confirm(`Are you sure you want to delete variant "${variant.variant_key}"?`);
    if (!confirmDelete) return;

    await this.commonApiService.deleteVariant(variant.variant_id);
    this.loadVariants();
  }
}
